/**
 * The `/content` screen's persisted state (§12.4, SPEC `Content Performance` § Frontend).
 *
 * `content:main` is loaded **before the first data fetch** — every content query stays disabled
 * until `ready` flips — defaulted on `404`, and written back debounced (500 ms). One global blob:
 * this screen has no subject to key on.
 *
 * `useViewUiState`'s machine, minus the per-subject re-seeding it needs and this one does not.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CONTENT_UI_STATE_KEY, UI_STATE_MAX_BYTES } from '@lw/shared';
import { getUiState, putUiState } from '../../api/uiState';
import { QK } from '../../api/queryKeys';
import { useDebouncedCallback, useLatest } from '../../lib/hooks';
import {
  DEFAULT_CONTENT_STATE,
  sanitizeContentState,
  toStoredContentState,
  type ContentState,
} from './contentState';

const WRITE_DEBOUNCE_MS = 500;

export type ContentUiStateHandle = {
  /** `false` until the stored value (or its 404) has arrived — gate every query on this. */
  ready: boolean;
  state: ContentState;
  update: (patch: Partial<ContentState>) => void;
  error: unknown;
  retry: () => void;
};

export function useContentUiState(): ContentUiStateHandle {
  const query = useQuery({
    queryKey: QK.uiState(CONTENT_UI_STATE_KEY),
    queryFn: ({ signal }) => getUiState<unknown>(CONTENT_UI_STATE_KEY, signal),
    staleTime: Number.POSITIVE_INFINITY,
    // A cached value from a previous visit is authoritative; refetching would race the writer.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const [state, setState] = useState<ContentState | null>(null);
  const seeded = useRef(false);

  // Seed once: from whatever the server had, from the defaults on 404, and — so a broken ui-state
  // endpoint can never brick the screen — from the defaults on failure too.
  useEffect(() => {
    if (seeded.current) return;
    if (query.isSuccess) {
      seeded.current = true;
      setState(sanitizeContentState(query.data));
      return;
    }
    if (query.isError) {
      seeded.current = true;
      setState(sanitizeContentState(null));
    }
  }, [query.isSuccess, query.isError, query.data]);

  const persist = useDebouncedCallback((next: ContentState) => {
    const payload = toStoredContentState(next);
    if (JSON.stringify(payload).length > UI_STATE_MAX_BYTES) return;
    void putUiState(CONTENT_UI_STATE_KEY, payload).catch(() => {
      // Persistence is best-effort: the screen keeps working with in-memory state.
    });
  }, WRITE_DEBOUNCE_MS);

  // Driven from a ref rather than from inside the updater: React skips updaters for an unmounted
  // component, and the last change before navigating away must still reach the server.
  const stateRef = useLatest(state);

  const update = useCallback(
    (patch: Partial<ContentState>) => {
      const next: ContentState = { ...(stateRef.current ?? DEFAULT_CONTENT_STATE), ...patch };
      setState(next);
      persist(next);
    },
    [persist, stateRef],
  );

  return {
    ready: state !== null,
    state: state ?? DEFAULT_CONTENT_STATE,
    update,
    error: query.isError ? query.error : null,
    retry: () => void query.refetch(),
  };
}
